import { z } from "zod";
import { adminSeatRepresentativeSchema } from "./admin-group-members.js";
import { groupCategoryEmbeddedSchema, groupCategoryTypeSchema } from "./groups.js";
import { ADMIN_COLLECTION_ACTION_REASON_MAX_LENGTH } from "./admin-collection-actions.js";

/** Read-only roster của một đợt/lịch thu. Mọi số tiền là string minor units. */
export const ADMIN_COLLECTION_PARTICIPANTS_MAX_LIMIT = 100;

/**
 * `upcoming` (US-AE4.F7-1011): người có trong danh sách LỊCH nhưng chưa có khoản nào (vừa được thêm — tính từ kỳ sau). Tiền = "0".
 */
export const adminCollectionParticipantStatusSchema = z.enum(["completed", "partial", "unpaid", "upcoming", "waived"]);
/**
 * US-AE4.F7-1012 — vì sao không "Xoá khỏi đợt" được: `all_members` (đợt / lịch toàn bộ nhóm) · `not_supported` (không phải đợt chọn
 * người chia đều) · `schedule_cancelled` · `money_touched` (đợt một lần: đã chạm tiền — luật huỷ đợt của app) · `last_participant`
 * (người cuối cùng — như app chặn danh sách rỗng) · `not_in_schedule` (đã không còn trong danh sách lịch).
 */
export const ADMIN_REMOVE_PARTICIPANT_LOCKS = [
  "all_members",
  "not_supported",
  "schedule_cancelled",
  "money_touched",
  "last_participant",
  "not_in_schedule",
] as const;
export type AdminRemoveParticipantLock = (typeof ADMIN_REMOVE_PARTICIPANT_LOCKS)[number];

export type AdminCollectionParticipantStatus = z.infer<
  typeof adminCollectionParticipantStatusSchema
>;

export const adminCollectionParticipantsQuerySchema = z
  .object({
    q: z.string().trim().max(256).optional(),
    status: adminCollectionParticipantStatusSchema.optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_COLLECTION_PARTICIPANTS_MAX_LIMIT).optional(),
  })
  .strict();
export type AdminCollectionParticipantsQuery = z.infer<
  typeof adminCollectionParticipantsQuerySchema
>;

export const adminCollectionParticipantItemSchema = z
  .object({
    memberId: z.string().uuid(),
    memberDisplayName: z.string().nullable(),
    expectedMinor: z.string().regex(/^\d+$/),
    paidMinor: z.string().regex(/^\d+$/),
    outstandingMinor: z.string().regex(/^\d+$/),
    currency: z.string().length(3),
    status: adminCollectionParticipantStatusSchema,
    representatives: z.array(adminSeatRepresentativeSchema),
  })
  .strict();
export type AdminCollectionParticipantItem = z.infer<typeof adminCollectionParticipantItemSchema>;

/**
 * US-AE4.F7-1012: dòng tóm tắt đầu tab Người tham gia (đợt đang xem; không phụ thuộc bộ lọc / trang) — chỉ người ĐANG thuộc diện phải
 * đóng: `payingCount` người phải đóng · `waivedCount` người được miễn khoản của đợt này · `memberCount` tổng thành viên thuộc diện.
 */
export const adminParticipantsSummarySchema = z
  .object({
    payingCount: z.number().int().nonnegative(),
    waivedCount: z.number().int().nonnegative(),
    memberCount: z.number().int().nonnegative(),
  })
  .strict();
export type AdminParticipantsSummary = z.infer<typeof adminParticipantsSummarySchema>;

export const adminCollectionParticipantsResponseSchema = z
  .object({
    groupType: groupCategoryTypeSchema.nullable(),
    groupCategory: groupCategoryEmbeddedSchema.nullable(),
    summary: adminParticipantsSummarySchema.optional(),
    data: z.array(adminCollectionParticipantItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();
export type AdminCollectionParticipantsResponse = z.infer<
  typeof adminCollectionParticipantsResponseSchema
>;

export const adminCollectionParticipantPeriodSchema = z
  .object({
    memberDueId: z.string().uuid(),
    collectionId: z.string().uuid(),
    title: z.string().nullable(),
    periodKey: z.string().nullable(),
    dueDate: z.string().nullable(),
    expectedMinor: z.string().regex(/^\d+$/),
    paidMinor: z.string().regex(/^\d+$/),
    outstandingMinor: z.string().regex(/^\d+$/),
    currency: z.string().length(3),
    status: adminCollectionParticipantStatusSchema,
    hasPendingConfirmation: z.boolean(),
    /**
     * Nút "Miễn" cho khoản này: khoản chưa miễn VÀ chưa có tiền vào — MỘT nguồn với chốt lúc miễn (`moneyInDueWhere` của API: đã ghi sổ ·
     * đã đóng > 0 · lời khai đang chờ · đề nghị nộp hộ đang chờ / đã chấp nhận — QĐ PO 08/10). Client không tự suy từ `status`.
     */
    waivable: z.boolean(),
  })
  .strict();
export type AdminCollectionParticipantPeriod = z.infer<
  typeof adminCollectionParticipantPeriodSchema
>;

/**
 * Chỉ các sự kiện đã xảy ra. Cố ý không có event kiểu `outstanding_changed`: còn thiếu là phép
 * tính và chỉ được hiển thị ở Tổng quan/Theo kỳ, không được giả làm lịch sử.
 */
export const adminCollectionParticipantHistoryKindSchema = z.enum([
  "participant_added",
  "payment_submitted",
  "payment_approved",
  "payment_rejected",
  "payment_recorded",
  "payment_reversed",
  "reminder_sent",
  // US-AE4.F7-1011: nhân viên thêm người vào đợt/lịch (đọc nhật ký admin) — `actorName` = email nhân viên, kèm `reason` + `appliesFrom`.
  "staff_added",
  // US-AE4.F7-1012: nhân viên gỡ khỏi đợt/lịch · miễn khoản · bỏ miễn (nhật ký admin) — email nhân viên + `reason`.
  "staff_removed",
  "staff_waived",
  "staff_unwaived",
]);
export type AdminCollectionParticipantHistoryKind = z.infer<
  typeof adminCollectionParticipantHistoryKindSchema
>;

export const adminCollectionParticipantHistoryItemSchema = z
  .object({
    id: z.string(),
    kind: adminCollectionParticipantHistoryKindSchema,
    at: z.string().datetime(),
    periodKey: z.string().nullable(),
    amountMinor: z.string().regex(/^\d+$/).nullable(),
    currency: z.string().length(3).nullable(),
    actorName: z.string().nullable(),
    /** US-AE4.F7-1011 — chỉ `staff_added`: lý do nhân viên ghi + nợ ngay (`now`) hay từ kỳ sau (`next_period`). */
    reason: z.string().nullable().optional(),
    appliesFrom: z.enum(["now", "next_period"]).nullable().optional(),
  })
  .strict();
export type AdminCollectionParticipantHistoryItem = z.infer<
  typeof adminCollectionParticipantHistoryItemSchema
>;

export const adminCollectionParticipantDetailSchema = z
  .object({
    memberId: z.string().uuid(),
    memberDisplayName: z.string().nullable(),
    expectedMinor: z.string().regex(/^\d+$/),
    paidMinor: z.string().regex(/^\d+$/),
    outstandingMinor: z.string().regex(/^\d+$/),
    currency: z.string().length(3),
    status: adminCollectionParticipantStatusSchema,
    completedPeriodCount: z.number().int().nonnegative(),
    partialPeriodCount: z.number().int().nonnegative(),
    unpaidPeriodCount: z.number().int().nonnegative(),
    pendingConfirmationCount: z.number().int().nonnegative(),
    representatives: z.array(adminSeatRepresentativeSchema),
    /** US-AE4.F7-1012: vì sao nút "Xoá khỏi đợt" mờ (`null` = gỡ được) — cùng hàm với lúc gỡ. */
    removeLock: z.enum(ADMIN_REMOVE_PARTICIPANT_LOCKS).nullable().optional(),
    /** US-AE4.F7-1012: suất còn trong nhóm (thuộc diện phải đóng) — "Bỏ miễn" chỉ khi `true`. */
    memberInGroup: z.boolean().optional(),
    /** US-AE4.F7-1012 (review #1285 H2): đợt cho Miễn / Bỏ miễn — chỉ đợt thu thường. */
    waiveAllowed: z.boolean().optional(),
    periods: z.array(adminCollectionParticipantPeriodSchema),
    history: z.array(adminCollectionParticipantHistoryItemSchema),
  })
  .strict();
export type AdminCollectionParticipantDetail = z.infer<
  typeof adminCollectionParticipantDetailSchema
>;

// ─────────────── US-AE4.F7-1011 (@UC9879) — màn Đợt thu thêm NHIỀU người vào MỘT đợt (cùng lõi với màn Nhóm) ───────────────

/** Tối đa số người mỗi lần thêm (QĐ người dùng 07/10/2026). */
export const ADMIN_ADD_PARTICIPANTS_MAX = 30;

/** GET …/:id/participant-candidates — ai thêm được: thuộc diện phải đóng và CHƯA có trong đợt (một lần) / lịch (định kỳ). */
export const adminParticipantCandidatesQuerySchema = z
  .object({
    q: z.string().trim().max(256).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_COLLECTION_PARTICIPANTS_MAX_LIMIT).optional(),
  })
  .strict();
export type AdminParticipantCandidatesQuery = z.infer<typeof adminParticipantCandidatesQuerySchema>;

export const adminParticipantCandidateItemSchema = z
  .object({
    memberId: z.string().uuid(),
    memberDisplayName: z.string().nullable(),
    representatives: z.array(adminSeatRepresentativeSchema),
  })
  .strict();
export type AdminParticipantCandidateItem = z.infer<typeof adminParticipantCandidateItemSchema>;

export const adminParticipantCandidatesResponseSchema = z
  .object({
    groupType: groupCategoryTypeSchema.nullable(),
    groupCategory: groupCategoryEmbeddedSchema.nullable(),
    data: z.array(adminParticipantCandidateItemSchema),
    pageInfo: z.object({ hasNext: z.boolean(), nextCursor: z.string().nullable() }).strict(),
  })
  .strict();
export type AdminParticipantCandidatesResponse = z.infer<typeof adminParticipantCandidatesResponseSchema>;

/** POST …/:id/participants — lý do bắt buộc, 1–30 người, không trùng. */
export const adminAddParticipantsBodySchema = z
  .object({
    reason: z.string().trim().min(1).max(ADMIN_COLLECTION_ACTION_REASON_MAX_LENGTH),
    memberIds: z
      .array(z.string().uuid())
      .min(1)
      .max(ADMIN_ADD_PARTICIPANTS_MAX)
      .refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length, { message: "Danh sách có người trùng" }),
  })
  .strict();
export type AdminAddParticipantsBody = z.infer<typeof adminAddParticipantsBodySchema>;

/** `now` = đợt một lần (nợ ngay) · `next_period` = lịch định kỳ (tính từ kỳ sau). */
export const adminAddParticipantsResponseSchema = z
  .object({
    collectionId: z.string(),
    addedMemberIds: z.array(z.string()),
    appliesFrom: z.enum(["now", "next_period"]),
  })
  .strict();
export type AdminAddParticipantsResponse = z.infer<typeof adminAddParticipantsResponseSchema>;

/**
 * 422 — có người không thêm được, KHÔNG ai được thêm. `params` (problem+json chỉ nhận giá trị PHẲNG) = mỗi lý do một khoá, giá trị
 * là danh sách memberId cách nhau dấu phẩy; lý do không có ai thì vắng khoá.
 */
export const ADMIN_ADD_PARTICIPANTS_INVALID_CODE = "ADMIN_ADD_PARTICIPANTS_INVALID" as const;
export const ADMIN_ADD_PARTICIPANT_INVALID_KEYS = ["notInGroup", "notObligated", "alreadyIn"] as const;
export type AdminAddParticipantInvalidKey = (typeof ADMIN_ADD_PARTICIPANT_INVALID_KEYS)[number];

// ─────────────── US-AE4.F7-1012 (@UC9879) — gỡ khỏi đợt · miễn / bỏ miễn từng khoản ───────────────

/** POST …/:id/participants/:memberId/remove — `due_deleted` (đợt một lần) · `removed_from_schedule` (định kỳ, từ kỳ sau). */
export const adminRemoveParticipantResponseSchema = z
  .object({
    collectionId: z.string(),
    memberId: z.string(),
    mode: z.enum(["due_deleted", "removed_from_schedule"]),
  })
  .strict();
export type AdminRemoveParticipantResponse = z.infer<typeof adminRemoveParticipantResponseSchema>;

/** POST …/:id/dues/:dueId/waive | unwaive. */
export const adminDueWaiveResponseSchema = z
  .object({ memberDueId: z.string(), status: z.enum(["waived", "pending"]) })
  .strict();
export type AdminDueWaiveResponse = z.infer<typeof adminDueWaiveResponseSchema>;

/** Mã 409 của miễn / bỏ miễn. Đã có tiền vào ⇒ dùng lại `COLLECTION_HAS_PAID_DUES` của app. */
export const ADMIN_DUE_WAIVE_CODE = {
  ALREADY_WAIVED: "ADMIN_DUE_ALREADY_WAIVED",
  NOT_WAIVED: "ADMIN_DUE_NOT_WAIVED",
  /** Bỏ miễn khi suất không còn trong nhóm ⇒ tạo món nợ không ai trả được. */
  MEMBER_NOT_IN_GROUP: "ADMIN_DUE_MEMBER_NOT_IN_GROUP",
  /** Không phải đợt thu thường (nợ với quỹ · quyết toán · mua chung gói) ⇒ không miễn tay (review #1285 H2). */
  NOT_SUPPORTED: "ADMIN_DUE_WAIVE_NOT_SUPPORTED",
} as const;
